// src/utils/lineNumbers.js
//
// View-only continuous line-number gutter for the editor (Google-Docs style).
//
// Rather than injecting numbers into the text flow (fragile under pagination),
// we paint an absolutely-positioned gutter per .rv-page and drop a number label
// at the top of every RENDERED line. Line positions are measured with
// Range.getClientRects(), so wrapped lines are numbered correctly. Numbering is
// CONTINUOUS across pages.
//
// The gutter is tagged with DECORATION_ATTR and marked contenteditable=false, so
// getCleanHTML() strips it from anything saved / broadcast / exported (see
// editorSerialize.js). Callers clear the gutter before structural DOM ops and
// rebuild once layout settles (mirrors formattingMarks.js).

import { DECORATION_ATTR } from './editorSerialize';

const GUTTER_CLASS = 'rv-line-gutter';
const SKIP_SEL = '.rv-end-spacer, .rv-page-header, .rv-page-footer';
const BLOCK_SEL = 'p, li, h1, h2, h3, h4, h5, h6, blockquote';

/** Remove every line-number gutter. Idempotent and lossless. */
export function clearLineNumbers(root) {
  if (!root) return;
  root.querySelectorAll(`.${GUTTER_CLASS}`).forEach((n) => n.remove());
}

// Collect the distinct rendered-line top positions (viewport coords) for a block.
function lineTopsForBlock(block) {
  const tops = [];
  const seen = new Set();
  const pushTop = (top) => {
    const key = Math.round(top);
    if (!seen.has(key)) { seen.add(key); tops.push(top); }
  };

  let rects = [];
  try {
    const range = document.createRange();
    range.selectNodeContents(block);
    rects = Array.from(range.getClientRects());
  } catch { /* fall through */ }

  if (rects.length === 0) {
    // Empty paragraph / placeholder <br> — still one visual line.
    const r = block.getBoundingClientRect();
    if (r.height) pushTop(r.top);
    return tops;
  }
  rects.forEach((r) => { if (r.height) pushTop(r.top); });
  return tops;
}

/**
 * Walk every page's numberable blocks in document order.
 *
 * Shared by the gutter and by buildLineIndex so the two can never disagree
 * about what counts as a line — a panel that says "Line 47" while the gutter
 * says 46 is worse than no line numbers at all.
 *
 * @param {Element} root editor host
 * @param {(page:Element, body:Element, blocks:Element[]) => void} visitPage
 */
function forEachPage(root, visitPage) {
  root.querySelectorAll('.rv-page').forEach((page) => {
    const body = page.querySelector('.rv-page-body');
    if (!body) return;
    const blocks = Array.from(body.children).filter(
      (el) => el.matches(BLOCK_SEL) && !el.matches(SKIP_SEL) && !el.closest(SKIP_SEL)
    );
    visitPage(page, body, blocks);
  });
}

/**
 * (Re)build the continuous line-number gutter. Always clears first, so repeated
 * calls never accumulate. Skips the block holding the caret is unnecessary here
 * because the gutter lives OUTSIDE the editable body and never touches text.
 *
 * @param {Element|null} root editor host
 */
export function applyLineNumbers(root) {
  if (!root) return;
  clearLineNumbers(root);

  let lineNo = 1;

  forEachPage(root, (page, body, blocks) => {
    const pageRect = page.getBoundingClientRect();
    const bodyRect = body.getBoundingClientRect();
    // Width of the left margin area we can paint into (between page edge and
    // where body content starts). Numbers are right-aligned within it.
    const gutterWidth = Math.max(bodyRect.left - pageRect.left, 24);

    const gutter = document.createElement('div');
    gutter.className = GUTTER_CLASS;
    gutter.setAttribute(DECORATION_ATTR, '');
    gutter.setAttribute('contenteditable', 'false');
    gutter.setAttribute('aria-hidden', 'true');
    gutter.style.width = `${gutterWidth - 6}px`;

    blocks.forEach((block) => {
      lineTopsForBlock(block).forEach((top) => {
        const label = document.createElement('div');
        label.className = 'rv-line-number';
        label.textContent = String(lineNo++);
        label.style.top = `${top - pageRect.top}px`;
        gutter.appendChild(label);
      });
    });

    page.appendChild(gutter);
  });
}

/**
 * Map each block to the line number of its FIRST rendered line, using exactly
 * the same counting rules as the gutter above.
 *
 * Line numbers are a property of layout, not of the document: they shift when
 * text is inserted, the font changes, or pagination reflows. So the Collab
 * comment index measures them fresh rather than trusting a stored value — a
 * comment's `line_hint` is only a fallback for blocks that no longer render.
 *
 * Blocks that render nothing (zero client rects) are intentionally left out:
 * the gutter paints no number for them, so neither should the panel.
 *
 * Cost is one getClientRects() pass over the document, so callers should
 * debounce this and only run it while the panel that needs it is open.
 *
 * @param {Element|null} root editor host
 * @returns {Map<string, number>} data-block-id → first line number
 */
export function buildLineIndex(root) {
  const index = new Map();
  if (!root) return index;

  let lineNo = 1;

  forEachPage(root, (page, body, blocks) => {
    blocks.forEach((block) => {
      const lineCount = lineTopsForBlock(block).length;
      if (lineCount === 0) return;
      const id = block.getAttribute('data-block-id');
      // First occurrence wins — ids should be unique (ensureBlockIds enforces
      // it), but a stale duplicate must not overwrite the earlier position.
      if (id && !index.has(id)) index.set(id, lineNo);
      lineNo += lineCount;
    });
  });

  return index;
}
